<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WorksheetEditor',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    worksheet: {
      type: Object,
      required: false,
    },
    target: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const getPayload = (...args) => {
    const [data] = args;
    const fields =
      props.target && targetConfig.value[props.target].fieldsToSubmit;
    return fields ? _.pick(data, fields) : data;
  };

  const formId = ref(null);
  const defaultTitle = ref($fn.value.pgettext('Worksheet', 'Edit Worksheet'));
  const targetConfig = ref({
    header: {
      title: $fn.value.pgettext('Worksheet', 'Edit Worksheet Header'),
      fieldsToSubmit: ['id', 'revision_id', 'etag', 'company'],
    },
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    defaultTitle: defaultTitle.value,
    targetConfig: targetConfig.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'defaultTitle' },
      { name: 'targetConfig' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId },
    ux: {},
    dataRefs: {
      formId,
      defaultTitle,
      targetConfig,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: props.target
          ? targetConfig.value[props.target].title
          : defaultTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );
  watch(
    () => props.target,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('target');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_mjez5ls8v_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_8564662037462133_closeComponent_n3o67w60i_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };
  const act_5534025912102772_closeModal_aatj05bon_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const act_5534025912102772_crud_expa8j9v1_getActionArgs = async ($event) => {
    return {
      objectType: 'worksheet',
      op: 'upsert',
      data: getPayload(props.worksheet),
    };
  };
  const act_2434514588044777_closeComponent_resv0jo3b_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_mjez5ls8v =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_mjez5ls8v_getActionArgs,
    });
  const inst_evh_7315092382398562_close_d5gntffy4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_mjez5ls8v,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_d5gntffy4],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_lhnrw6gx5 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_7sj5tj4az =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_lhnrw6gx5,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_7sj5tj4az],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_n3o67w60i =
    new actionTypes.CloseComponentAction({
      actionArgs: act_8564662037462133_closeComponent_n3o67w60i_getActionArgs,
    });
  const inst_evh_8564662037462133_close_jc4ozeifx =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_n3o67w60i,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_jc4ozeifx],
    event: 'close',
  });
  const act_5534025912102772_closeModal_aatj05bon =
    new actionTypes.CloseModalAction({
      actionArgs: act_5534025912102772_closeModal_aatj05bon_getActionArgs,
    });
  const inst_evh_5534025912102772_success_yqjk6ddu2 =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_closeModal_aatj05bon,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_5534025912102772_crud_expa8j9v1 = new actionTypes.CRUDAction({
    actionArgs: act_5534025912102772_crud_expa8j9v1_getActionArgs,
    displayName: 'save',
    events: [inst_evh_5534025912102772_success_yqjk6ddu2],
  });
  const inst_evh_5534025912102772_submit_i8jvu3dix =
    new actionTypes.EventHandlerImpl({
      action: act_5534025912102772_crud_expa8j9v1,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_5534025912102772_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5534025912102772_submit_i8jvu3dix],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_resv0jo3b =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2434514588044777_closeComponent_resv0jo3b_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_flka0kxri =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_resv0jo3b,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_flka0kxri],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_d5gntffy4,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_7sj5tj4az,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_jc4ozeifx,
    inst_evh_8564662037462133_close,
    inst_evh_5534025912102772_success_yqjk6ddu2,
    inst_evh_5534025912102772_submit_i8jvu3dix,
    save: inst_evh_5534025912102772_submit_i8jvu3dix,
    inst_evh_5534025912102772_submit,
    inst_evh_2434514588044777_clickPreventStop_flka0kxri,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_d5gntffy4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_d5gntffy4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_7sj5tj4az = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_7sj5tj4az.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_jc4ozeifx = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_jc4ozeifx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_success_yqjk6ddu2 = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_success_yqjk6ddu2.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit_i8jvu3dix = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit_i8jvu3dix.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5534025912102772_submit = async (event, scope) => {
    return await actions.inst_evh_5534025912102772_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_flka0kxri = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_flka0kxri.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WorksheetEditor"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :color="$config.worksheet.color"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <div class="page d-flex flex-column">
      <div class="page__content container-fluid pt-3 h-100">
        <ItpForm
          :id="formId"
          name="worksheet"
          class="row"
          ref="mainForm"
          @submit="evh_5534025912102772_submit($event, {})"
        >
          <ItpCol>
            <UcWorksheetEditorFieldset
              :worksheet="worksheet"
              :formId="formId"
              :target="target"
            >
            </UcWorksheetEditorFieldset>
          </ItpCol>
        </ItpForm>
      </div>
    </div>
    <template #footer>
      <UcFooter
        :card="!modal"
        :modal="modal"
        :color="$config.worksheet.color"
        v-if="loaded"
      >
        <ItpButton
          :text="pgettext('Button', 'Save changes')"
          type="submit"
          :form="forms.worksheet"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
